import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./Login.module.css";
import { Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import api from "../../api/axois"; // Adjust the import path as necessary
import GoogleLoginButton from "../../components/GoogleLoginButton";
import { useUser } from "../../context/UserContext";

const EyeIcon = ({ open }) =>
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useUser();

  useEffect(() => {
    const verified = searchParams.get('verified');
    if (verified === 'true') {
      setSuccess("Email verified successfully! You can now log in.");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    
    try {
      const response = await api.post("/login", { email, password });
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      setUser(response.data.user); // <-- update context immediately

      // Route based on user role
      const role = response.data.user?.role;
      if (role === "admin" || role === "super_admin") {
        navigate("/admin/BookingManagement");
      } else {
        navigate("/");
      }
    } catch (err) {
      // Handle email verification required
      if (err.response?.data?.email_verification_required) {
        setError("Please verify your email address before logging in.");
        // Optionally redirect to verification page
        setTimeout(() => {
          navigate("/VerificationPage");
        }, 2000);
        setLoading(false);
        return;
      }

      // The main Aurelia account system and the ChopNation (dining/kitchen)
      // account system are separate — a kitchen staff or dining customer
      // account doesn't exist in the main `users` table. If the primary
      // login fails, try ChopNation before giving up so this one login
      // page works for both.
      try {
        const chopResponse = await api.post("/chopnation/login", { email, password });
        const userData = chopResponse.data.user;
        const clientUser = { ...userData, role: userData.role || "customer" };

        localStorage.setItem("token", chopResponse.data.access_token);
        localStorage.setItem("user", JSON.stringify(clientUser));
        setUser(clientUser);

        if (clientUser.role === "kitchen_staff") {
          navigate("/kitchen/dashboard");
        } else if (clientUser.role === "admin" || clientUser.role === "super_admin") {
          navigate("/admin/BookingManagement");
        } else {
          navigate("/dining");
        }
        return;
      } catch {
        setError(
          err.response?.data?.message ||
          err.response?.data?.errors?.email?.[0] ||
          "Login failed. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackHome = () => {
    navigate("/");
  };

  return (
    <main className="main-content">
      <section className={styles.container}>
        <h2 className={styles.heading}>Login</h2>
        <form className={styles.form} onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="Email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
        <div style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={{ width: "100%", paddingRight: "2.5rem", boxSizing: "border-box" }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            style={{
              position: "absolute",
              right: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              color: "#888",
              display: "flex",
              alignItems: "center",
            }}
          >
            <EyeIcon open={showPassword} />
          </button>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}
        <button type="submit" className={styles.btn} disabled={loading}>
          {loading ? "Logging in..." : "LOGIN"}
        </button>
        <div style={{ marginTop: 10, textAlign: 'center' }}>
          <Link to="/forgot-password">Forgot password?</Link>
        </div>
        <button
          type="button"
          className={styles.btn}
          style={{ marginTop: "10px", background: "#eee", color: "#333" }}
          onClick={handleBackHome}
        >
          Back to Homepage
        </button>
        <div className={styles.separator}>
          <hr />
          <span>Or</span>
          <hr />
        </div>
        <GoogleLoginButton></GoogleLoginButton>
        </form>
      </section>
    </main>
  );
};

export default Login;
